import { ImageLabelLineProps } from 'components/shared'
import { SelectOption } from 'components/shared/Select'
import { action, computed, observable } from 'mobx'
import {
  BrandDTO,
  GraphUserDTO,
  Image,
  PersonDTO,
  Project,
  ProjectDTO,
  UserDTO,
  UserRoleDTO,
  UserRoleName
} from 'models'
import { GENERAL_STORE } from 'store/GeneralStore'
import {
  INVITE_USER_STORE,
  LinkTo,
  personEditRoleId,
  roleIdsWithNoProjectsAssignmentOption,
  shopOwnerRoleId
} from 'store/InviteUserStore'
import { PROJECTS_TABLE_STORE } from 'store/ProjectsTableStore'
import { PROJECT_STORE } from 'store/ProjectStore'
import { RootStore } from 'store/RootStore'
import { Api, getMainImageSrcForObj, noImageAvatar, User } from 'utilities'

export const USER_PROFILE_STORE = 'USER_PROFILE_STORE'

export class UserProfileStore {
  private _rootStore: RootStore

  constructor (rootStore: RootStore) {
    this._rootStore = rootStore
  }

  private _userUnchanged: UserDTO = null
  private _assignmentsUnchanged: GraphUserDTO = null
  @observable user: UserDTO = null
  @observable assignedPerson: PersonDTO = null
  @observable assignedStore: BrandDTO = null
  @observable private _assignedProjectIds: Set<number> = new Set()

  @action setUser = (user: UserDTO) => {
    this.user = user
    this._userUnchanged = user
    this._loadUserAssignments()
    this._loadLinkToEntityOptions()
  }

  private _loadLinkToEntityOptions = () => {
    if (!this.user) return
    // PERSON_EDIT_ONLY users cannot edit their own role/linked entity from My Profile,
    // so loading the entity options list is unnecessary and only adds API noise.
    if (this._isLoggedInUserPersonEditOnly) return

    const {
      loadPeople,
      loadBrands
    } = this._rootStore[INVITE_USER_STORE]

    if (this._isPersonEditRoleSelected) {
      loadPeople()
    } else if (this._isShopOwnerRoleSelected) {
      loadBrands()
    }
  }

  @action setAssignedStore = (store: BrandDTO) => {
    this.assignedStore = store
  }

  @action setAssignedPerson = (person: PersonDTO) => {
    this.assignedPerson = person
  }

  @action private _setAssignedProjects = (projectIdsSet: Set<number>) => {
    this._assignedProjectIds = projectIdsSet
  }

  @action assignProject = (projectId: number) => {
    this._assignedProjectIds.add(projectId)
    this._refreshAssignedProjectTable()
    this._applyAssignedProjectsChange()
  }

  @action setUserProp = (props: Partial<UserDTO>) => {
    this.user = {
      ...this.user,
      ...props
    }
  }

  private _refreshAssignedProjectTable = () => {
    const {
      [PROJECTS_TABLE_STORE]: {
        // setSearchResults,
        triggerSearch
      },
      [PROJECT_STORE]: {
        setProjectsList
      },
      [INVITE_USER_STORE]: {
        getProjectById
      }
    } = this._rootStore

    setProjectsList(
      Array.from(this._assignedProjectIds.values())
        .map(id => new Project(
          getProjectById(id)
        ))
    )
    triggerSearch()
  }

  getUser = (userId: string | number) => {
    const {
      setLoadingOn,
      setLoadingOff,
      handleApiError
    } = this._rootStore[GENERAL_STORE]

    setLoadingOn()
    Api
      .get<UserDTO>(`/users/${userId}`, 'userApi')
      .then(data => {
        this.setUser(data)
        setLoadingOff()
      })
      .catch(handleApiError)
  }

  onNameChange = (fullName: string) => {
    const names = fullName.split(' ')
    const firstName = names.shift().trim()
    const lastName = names.join(' ').trim()
    this.setUserProp({
      firstName,
      lastName
    })
  }

  onUserRoleSelect = (roleId: number) => {
    const {
      getUserRoleById,
      loadBrands,
      loadPeople,
      loadProjects
    } = this._rootStore[INVITE_USER_STORE]
    const selectedRole = getUserRoleById(roleId)
    this.setUserProp({ roles: [selectedRole] })
    // this.setLinkToEntityId(null)

    switch (true) {
      case this._isPersonEditRoleSelected: {
        loadPeople()
        this.setAssignedStore(null)
        break
      }
      case this._isShopOwnerRoleSelected: {
        loadBrands()
        this.setAssignedPerson(null)
        break
      }
      default: {
        loadProjects()
        this.setAssignedPerson(null)
        this.setAssignedStore(null)
        break
      }
    }
  }

  onLinkToEntitySelect = (entityId: number) => {
    const entity = this._linkToEntitiesMap.get(entityId)
    this._isPersonEditRoleSelected
      ? this.setAssignedPerson(entity)
      : this.setAssignedStore(entity)
  }

  onDiscardChanges = () => {
    this.setUser(this._userUnchanged)
  }

  onSaveChanges = async (images: Image[]) => {
    const {
      setLoadingOn,
      setLoadingOff,
      handleApiError
    } = this._rootStore[GENERAL_STORE]

    if (images.length) {
      this.setUserProp({ imageUrl: images[0].url })
    } else {
      this.setUserProp({ imageUrl: null })
    }

    setLoadingOn()
    await this._applyUserRoleChange()
    await Api
      .post('/user/save', this.user, 'userApi')
      .then(async (updatedUser: UserDTO) => {
        if (updatedUser.id === User.getId()) {
          User.setUserDetails(updatedUser)
        }

        if (this.isLinkToEntityRoleSelected) {
          await Promise.all([
            this._applyStoreAssignmentChange(),
            this._applyPersonAssignmentChange()
          ]
          )
        } else {
          await this._applyAssignedProjectsChange()
        }

        this.setUser(updatedUser)
      })
      .catch(handleApiError)

    setLoadingOff()
  }

  clearStore = () => {
    this.setUser(null)
    this.setAssignedPerson(null)
    this.setAssignedStore(null)
    this._setAssignedProjects(new Set())
  }

  // @action assignProject = (projectId: number) => {
  //   this._assignedProjectIds.add(projectId)
  //   this._refreshAssignedProjectTable()
  // }

  private _applyStoreAssignmentChange = async () => {
    if (!this._previouslyAssignedBrand) {
      if (this.assignedStore) { // Newly assigned store
        await Api.post(
          `/permissions/users/${this.userId}/brands/${this.assignedStore.id}`,
          undefined
        )
      }
    } else {
      if (!this.assignedStore) { // Unassign store
        await Api.delete(
          `/permissions/users/${this.userId}/brands/${this._previouslyAssignedBrand.id}`,
          undefined
        )
      } else if (this._previouslyAssignedBrand.id !== this.assignedStore.id) { // Change the assigned store
        await Promise.all([
          Api.delete(
            `/permissions/users/${this.userId}/brands/${this._previouslyAssignedBrand.id}`,
            undefined
          ),
          Api.post(
            `/permissions/users/${this.userId}/brands/${this.assignedStore.id}`,
            undefined
          )
        ])
      }
    }
  }

  private _applyPersonAssignmentChange = async () => {
    if (!this._previouslyAssignedPerson) {
      if (this.assignedPerson) { // Newly assigned
        await Api.post(
          `/permissions/users/${this.userId}/people/${this.assignedPerson.id}`,
          undefined
        )
      }
    } else {
      if (!this.assignedPerson) { // Unassign
        await Api.delete(
          `/permissions/users/${this.userId}/people/${this._previouslyAssignedPerson.id}`,
          undefined
        )
      } else if (this._previouslyAssignedPerson.id !== this.assignedPerson.id) { // Change the assigned
        await Promise.all([
          Api.delete(
            `/permissions/users/${this.userId}/people/${this._previouslyAssignedPerson.id}`,
            undefined
          ),
          Api.post(
            `/permissions/users/${this.userId}/people/${this.assignedPerson.id}`,
            undefined
          )
        ])
      }
    }
  }

  private _applyAssignedProjectsChange = async () => {
    const previouslyAssignedProjectIds = this._assignmentsUnchanged
      ? (this._assignmentsUnchanged.projects || []).map(p => p.id)
      : []

    const projectIdsToUnassign = new Set<number>()
    const projectIdsToAssign = new Set<number>()

    previouslyAssignedProjectIds.forEach(id => {
      if (!this._assignedProjectIds.has(id)) {
        projectIdsToUnassign.add(id)
      }
    })
    this._assignedProjectIds.forEach(id => {
      if (!previouslyAssignedProjectIds.includes(id)) {
        projectIdsToAssign.add(id)
      }
    })

    const {
      [GENERAL_STORE]: {
        handleApiError
      },
      [INVITE_USER_STORE]: {
        getProjectById
      }
    } = this._rootStore

    await Promise.all([
      Promise.all(
        Array.from(projectIdsToUnassign.values())
          .map(projectId =>
            Api.delete(
              `/permissions/users/${this.userId}/projects/${projectId}`,
              undefined
            )
          )
      ),
      Promise.all(
        Array.from(projectIdsToAssign.values())
          .map(projectId =>
            Api.post(
              `/permissions/users/${this.userId}/projects/${projectId}`,
              undefined
            )
          )
      )
    ])
      .catch(handleApiError)

    if (projectIdsToUnassign.size || projectIdsToAssign.size) {
      this._assignmentsUnchanged = {
        ...this._assignmentsUnchanged,
        projects: Array.from(this._assignedProjectIds.values())
          .map(getProjectById)
      }
    }
  }

  private _applyUserRoleChange = async () => {
    const {
      roles = [null]
    } = this._userUnchanged

    const roleBeforeChange = roles[0]

    if (
      (!roleBeforeChange && !this.userRole) ||
      roleBeforeChange.id === this.userRole.id
    ) return

    await Api
      .put(
        `/user/${this.userId}/roles`,
        [this.userRole],
        'userApi'
      )
  }

  private _loadUserAssignments = () => {
    if (!this.user) return
    // PERSON_EDIT_ONLY users do not have access to /permissions/users/{userId} (it returns 403).
    // Skip the request to avoid showing a forbidden error popup when they open My Profile.
    if (this._isLoggedInUserPersonEditOnly) return

    const {
      handleApiError
    } = this._rootStore[GENERAL_STORE]

    Api
      .get<GraphUserDTO>(
        `/permissions/users/${this.user.id}`
      )
      .then(data => {
        this._assignmentsUnchanged = data
        const {
          brands,
          persons,
          projects
        } = data
        if (brands.length > 0) this.setAssignedStore(brands[0])
        if (persons.length > 0) this.setAssignedPerson(persons[0])
        this._handleAssignedProjects(projects)
      })
      .catch(handleApiError)
  }

  private _handleAssignedProjects = (projects: ProjectDTO[]) => {
    const {
      [PROJECT_STORE]: {
        setProjectsList
      },
      [INVITE_USER_STORE]: {
        loadProjects
      },
      [PROJECTS_TABLE_STORE]: {
        triggerSearch
      }
    } = this._rootStore
    const assignedProjectIds = new Set<number>()
    setProjectsList(
      projects.map(p => {
        assignedProjectIds.add(p.id)
        return new Project(p)
      })
    )

    this._setAssignedProjects(assignedProjectIds)
    triggerSearch()

    if (this.canAssignProjects) loadProjects()
  }

  private get _previouslyAssignedBrand (): BrandDTO {
    if (
      !this._assignmentsUnchanged ||
      !this._assignmentsUnchanged.brands ||
      !this._assignmentsUnchanged.brands.length
    ) return null

    return this._assignmentsUnchanged.brands[0]
  }

  private get _previouslyAssignedPerson (): PersonDTO {
    if (
      !this._assignmentsUnchanged ||
      !this._assignmentsUnchanged.persons ||
      !this._assignmentsUnchanged.persons.length
    ) return null

    return this._assignmentsUnchanged.persons[0]
  }

  onGoToLinkedEntityPageClick = () => {
    const {
      push
    } = this._rootStore[GENERAL_STORE]

    const route = this.linkToEntityType === LinkTo.PERSON
      ? 'persons'
      : 'brands/edit'
    push(`/${route}/${this.linkToEntity.id}`)
  }

  @computed get imageUrl (): string {
    if (!this.user || !this.user.imageUrl) return noImageAvatar
    return this.user.imageUrl
  }

  @computed get hasAssignedProjects (): boolean {
    return this._assignedProjectIds.size > 0
  }

  @computed get userRole (): UserRoleDTO {
    if (!this.user || this.user.roles.length < 1) return null
    return this.user.roles[0]
  }

  @computed get canAssignProjects (): boolean {
    if (!this.userRole) return false
    return !roleIdsWithNoProjectsAssignmentOption.includes(this.userRole.id)
  }

  @computed private get _isPersonEditRoleSelected (): boolean {
    if (!this.userRole) return false
    return this.userRole.id === personEditRoleId
  }

  private get _isLoggedInUserPersonEditOnly (): boolean {
    return User.userRoleNames.includes(UserRoleName.PERSON_EDIT_ONLY)
  }

  @computed private get _isShopOwnerRoleSelected (): boolean {
    if (!this.userRole) return false
    return this.userRole.id === shopOwnerRoleId
  }

  @computed get isLinkToEntityRoleSelected (): boolean {
    return this._isPersonEditRoleSelected ||
      this._isShopOwnerRoleSelected
  }

  @computed get linkToEntityType (): string {
    switch (true) {
      case this._isPersonEditRoleSelected: {
        return LinkTo.PERSON
      }
      case this._isShopOwnerRoleSelected: {
        return LinkTo.STORE
      }
      default: return ' '
    }
  }

  @computed get linkToEntity (): BrandDTO | PersonDTO {
    if (this._isShopOwnerRoleSelected) {
      return this.assignedStore
    } else if (this._isPersonEditRoleSelected) {
      return this.assignedPerson
    } return null
  }

  @computed get selectedLinkToEntityId (): number {
    if (!this.linkToEntity) return '' as any
    return this.linkToEntity.id
  }

  @computed private get _linkToEntitiesMap (): Map<number, any> {
    const {
      getLinkToEntitiesMap
    } = this._rootStore[INVITE_USER_STORE]
    return getLinkToEntitiesMap(this._isPersonEditRoleSelected)
  }

  @computed get linkToEntityOptions (): SelectOption<number, ImageLabelLineProps>[] {
    return Array.from(this._linkToEntitiesMap.values()).map(entity => ({
      label: entity.name,
      value: entity.id,
      customComponentProps: {
        imgSrc: getMainImageSrcForObj(entity),
        label: entity.name
      }
    }))
  }

  @computed get fullName (): string {
    if (!this.user) return ''
    return (this.user.firstName || '') + ' ' + (this.user.lastName || '')
  }

  @computed get userId (): number {
    if (!this.user) return null
    return this.user.id
  }
}
